// src/components/SecMasterHistory.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { HistoryGrid } from '@1823-partners/core';
import { fetchSecMasterEntry } from '../api';
import { formatDate } from '../utils';

const FIELD_NAMES = {
  CUSIP_PPN: 'CUSIP/PPN',
  SECURITY_DESCRIPTION: 'Security Description',
  SECURITY_OWNER: 'Security Owner',
  SECURITY_OWNER_TEAM: 'Security Owner Team',
  TICKER: 'Ticker',
  ISSUER: 'Issuer',
  BORROWER: 'Borrower',
  ASSET_TYPE: 'Asset Type',
  SECURITY_TYPE: 'Security Type',
  CURRENCY: 'Currency',
  STATUS: 'Status',
  TRADE_DATE: 'Trade Date',
  SETTLE_DATE: 'Settle Date',
  MATURITY_DATE: 'Maturity Date',
  SETTLEMENT_TYPE: 'Settlement Type',
  CLOSING_CONTACT: 'Closing Contact',
  TRADE_PRICE: 'Trade Price',
  STAT_BOOK_VALUE: 'Book Value',
  STAT_CURRENT_UNITS: 'Current Units',
  TOTAL_COMMITMENT: 'Total Commitment',
  COUPON_TYPE: 'Coupon Type',
  FLOATER: 'Floater (Y/N)',
  FLOATER_SPREAD: 'Floater Spread',
  REFERENCE_INDEX: 'Reference Index',
  PIK_INTEREST: 'PIK Interest',
  COUPON_FREQUENCY: 'Coupon Frequency',
  FIRST_COUPON_DATE: 'First Coupon Date',
  DAY_COUNT: 'Day Count',
  BALLOON_PAYMENT: 'Balloon Payment (Y/N)',
  ILLIQUID: 'Illiquid (Y/N)',
  ALL_IN_RATE: 'All-In Rate',
  BASE_RATE: 'Base Rate',
  STAT_BOOK_YIELD: 'Book Yield',
  AMORTIZATION_TYPE: 'Amortization Type',
  ACCRUED_INTEREST_START_DATE: 'Accrued Interest Start Date',
  RATING: 'Rating',
  NAIC_DESIGNATION: 'NAIC Designation',
  RATING_AGENCY: 'Rating Agency',
  PRIVATE_RATING: 'Private Rating (Y/N)',
  RATING_DATE: 'Rating Date',
  RATING_ESTIMATE: 'Rating Estimate (Y/N)',
  COUNTRY_OF_ISSUE: 'Country of Issue',
  AFFILIATED_UNAFFILIATED: 'Affiliated/Unaffiliated',
  CONNECTED_ENTITY: 'Connected Entity',
  COLLATERAL_TYPE: 'Collateral Type',
  NAIC_GENERAL_CATEGORY: 'NAIC General Category',
  STAT_CLASSIFICATION: 'STAT Classification',
  CW_ACCOUNT_NAME: 'CW Account Name',
  CW_ACCOUNT_NUMBER: 'CW Account #',
  CUSTODIAN_NAME: 'Custodian Name',
  CUSTODIAN_ACCOUNT_NUMBER: 'Custodian Account #',
  BROKER_DEALER: 'Broker/Dealer',
  SERVICE_FEE: 'Service Fee',
  INTEREST_ONLY_TERM: 'Interest Only Term',
  ORIGINAL_LOAN_TERM: 'Original Loan Term',
  RECOURSE_TYPE: 'Recourse Type',
  COMMENTS: 'Comments',
  ISSUE_DATE: 'Issue Date',
};

/** Format a numeric value with fixed decimal places. */
const fmtNumber = (decimals) => (params) => {
  if (params.value == null) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(params.value);
};

/** Format a boolean value as Y/N. */
const fmtYN = (params) => {
  if (params.value === true) return 'Y';
  if (params.value === false) return 'N';
  return '';
};

/** Format a numeric value as a percentage. */
const fmtPercent = (params) => {
  if (params.value == null) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(params.value);
};

const COLUMNS = [
  { headerName: 'CUSIP/PPN', field: 'CUSIP_PPN', pinned: 'left', width: 120 },
  { headerName: 'Security Description', field: 'SECURITY_DESCRIPTION', width: 200 },
  { headerName: 'Security Owner', field: 'SECURITY_OWNER', width: 120 },
  { headerName: 'Security Owner Team', field: 'SECURITY_OWNER_TEAM', width: 120 },
  { headerName: 'Ticker', field: 'TICKER', width: 100 },
  { headerName: 'Issuer', field: 'ISSUER', width: 150 },
  { headerName: 'Borrower', field: 'BORROWER', width: 150 },
  { headerName: 'Asset Type', field: 'ASSET_TYPE', width: 120 },
  { headerName: 'Security Type', field: 'SECURITY_TYPE', width: 120 },
  { headerName: 'Currency', field: 'CURRENCY', width: 80 },
  { headerName: 'Status', field: 'STATUS', width: 100 },
  { headerName: 'Trade Date', field: 'TRADE_DATE', width: 120 },
  { headerName: 'Settle Date', field: 'SETTLE_DATE', width: 120 },
  { headerName: 'Maturity Date', field: 'MATURITY_DATE', width: 120 },
  { headerName: 'Settlement Type', field: 'SETTLEMENT_TYPE', width: 120 },
  { headerName: 'Closing Contact', field: 'CLOSING_CONTACT', width: 200 },
  { headerName: 'Trade Price', field: 'TRADE_PRICE', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(2) },
  { headerName: 'Book Value', field: 'STAT_BOOK_VALUE', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(2) },
  { headerName: 'Current Units', field: 'STAT_CURRENT_UNITS', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(2) },
  { headerName: 'Total Commitment', field: 'TOTAL_COMMITMENT', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(2) },
  { headerName: 'Coupon Type', field: 'COUPON_TYPE', width: 120 },
  { headerName: 'Floater (Y/N)', field: 'FLOATER', width: 100, valueFormatter: fmtYN },
  { headerName: 'Floater Spread', field: 'FLOATER_SPREAD', width: 120 },
  { headerName: 'Reference Index', field: 'REFERENCE_INDEX', width: 150 },
  { headerName: 'PIK Interest', field: 'PIK_INTEREST', width: 120 },
  { headerName: 'Coupon Frequency', field: 'COUPON_FREQUENCY', width: 120 },
  { headerName: 'First Coupon Date', field: 'FIRST_COUPON_DATE', width: 120 },
  { headerName: 'Day Count', field: 'DAY_COUNT', width: 100 },
  { headerName: 'Balloon Payment (Y/N)', field: 'BALLOON_PAYMENT', width: 120, valueFormatter: fmtYN },
  { headerName: 'Illiquid (Y/N)', field: 'ILLIQUID', width: 100, valueFormatter: fmtYN },
  { headerName: 'All-In Rate', field: 'ALL_IN_RATE', width: 120 },
  { headerName: 'Base Rate', field: 'BASE_RATE', width: 120 },
  { headerName: 'Book Yield', field: 'STAT_BOOK_YIELD', width: 120 },
  { headerName: 'Amortization Type', field: 'AMORTIZATION_TYPE', width: 150 },
  { headerName: 'Accrued Interest Start Date', field: 'ACCRUED_INTEREST_START_DATE', width: 150 },
  { headerName: 'Rating', field: 'RATING', width: 100 },
  { headerName: 'NAIC Designation', field: 'NAIC_DESIGNATION', width: 120 },
  { headerName: 'Rating Agency', field: 'RATING_AGENCY', width: 120 },
  { headerName: 'Private Rating (Y/N)', field: 'PRIVATE_RATING', width: 120, valueFormatter: fmtYN },
  { headerName: 'Rating Date', field: 'RATING_DATE', width: 120 },
  { headerName: 'Rating Estimate (Y/N)', field: 'RATING_ESTIMATE', width: 120, valueFormatter: fmtYN },
  { headerName: 'Country of Issue', field: 'COUNTRY_OF_ISSUE', width: 120 },
  { headerName: 'Affiliated/Unaffiliated', field: 'AFFILIATED_UNAFFILIATED', width: 150 },
  { headerName: 'Connected Entity', field: 'CONNECTED_ENTITY', width: 150 },
  { headerName: 'Collateral Type', field: 'COLLATERAL_TYPE', width: 150 },
  { headerName: 'NAIC General Category', field: 'NAIC_GENERAL_CATEGORY', width: 150 },
  { headerName: 'STAT Classification', field: 'STAT_CLASSIFICATION', width: 150 },
  { headerName: 'CW Account Name', field: 'CW_ACCOUNT_NAME', width: 150 },
  { headerName: 'CW Account #', field: 'CW_ACCOUNT_NUMBER', width: 120 },
  { headerName: 'Custodian Name', field: 'CUSTODIAN_NAME', width: 150 },
  { headerName: 'Custodian Account #', field: 'CUSTODIAN_ACCOUNT_NUMBER', width: 150 },
  { headerName: 'Broker/Dealer', field: 'BROKER_DEALER', width: 150 },
  { headerName: 'Service Fee', field: 'SERVICE_FEE', type: 'numericColumn', width: 120, valueFormatter: fmtPercent },
  { headerName: 'Interest Only Term', field: 'INTEREST_ONLY_TERM', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(0) },
  { headerName: 'Original Loan Term', field: 'ORIGINAL_LOAN_TERM', type: 'numericColumn', width: 120, valueFormatter: fmtNumber(0) },
  { headerName: 'Recourse Type', field: 'RECOURSE_TYPE', width: 120 },
  { headerName: 'Comments', field: 'COMMENTS', width: 200 },
];

/** Date fields that need to be formatted before display. */
const DATE_FIELDS = [
  'TRADE_DATE',
  'SETTLE_DATE',
  'MATURITY_DATE',
  'ISSUE_DATE',
  'FIRST_COUPON_DATE',
  'RATING_DATE',
  'ACCRUED_INTEREST_START_DATE',
];

/** Transform raw API data by formatting date fields. */
const transformData = (data) =>
  data.map((entry) => {
    const formatted = { ...entry };
    DATE_FIELDS.forEach((field) => {
      if (formatted[field]) {
        formatted[field] = formatDate(formatted[field]);
      }
    });
    return formatted;
  });

const SecMasterHistory = ({ cusip_ppn, showTitle = true }) => {
  if (!cusip_ppn) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body1">
          Please select a sec master entry above to view history.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {showTitle && (
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sec Master History
        </Typography>
      )}
      <Box sx={{ height: '45vh' }}>
        <HistoryGrid
          fetchHistory={() => fetchSecMasterEntry(cusip_ppn)}
          fetchParams={[cusip_ppn]}
          columns={COLUMNS}
          fieldNames={FIELD_NAMES}
          transformData={transformData}
          emptyMessage="No sec master history available."
        />
      </Box>
    </Box>
  );
};

export default SecMasterHistory;
